import { FC, useCallback } from 'react';
import {
    DefaultFooterComponent,
    Dropdown,
    ListItemProps,
    PopupFooterProps,
    PopupStateProps
} from '@silverfort/sf-components';
import { usePopup } from '../context/PopupProvider';
import { addToDate } from '../../../utils/dates';
import { SingleDatePicker, TimePicker } from '../components/UserDrawerContent/components/DateTimePickers';

interface UseExcludePopupParams {
    title: string;
    description: string;
    customExcludeTitle?: string;
    customExcludeDescription: string;
    onExclude: (excludeTill: Date) => void;
}

interface UseExcludePopupReturnType {
    openExcludePopup: () => void;
}

enum ExcludeOption {
    OneHour = 'oneHour',
    OneDay = 'oneDay',
    SevenDays = 'sevenDays',
    Custom = 'custom'
}

interface ExcludePopupState {
    excludeOption: ExcludeOption;
}

interface CustomExcludePopupState {
    customExcludeDateTime?: Date;
}

type ExcludeByOptionContentProps = PopupStateProps<ExcludePopupState> & {
    description: string;
    onSelectCustomExclude: () => void;
};

type CustomExcludeContentProps = PopupStateProps<CustomExcludePopupState> & {
    customExcludeDescription: string;
};

type CustomExcludeFooterProps = PopupFooterProps<CustomExcludePopupState>;

const getExcludeTillForOption = (option: Exclude<ExcludeOption, ExcludeOption.Custom>, fromDate: Date): Date => {
    switch (option) {
        case ExcludeOption.OneHour:
            return addToDate(fromDate, 'hours', 1);
        case ExcludeOption.OneDay:
            return addToDate(fromDate, 'days', 1);
        case ExcludeOption.SevenDays:
            return addToDate(fromDate, 'days', 7);
        default:
            return fromDate;
    }
};

const DROPDOWN_ITEMS: ListItemProps<ExcludeOption>[] = [
    {
        value: ExcludeOption.OneHour,
        title: '1 hour'
    },
    {
        value: ExcludeOption.OneDay,
        title: '1 day'
    },
    {
        value: ExcludeOption.SevenDays,
        title: '7 days'
    },
    {
        value: ExcludeOption.Custom,
        title: 'Custom'
    }
];

const ExcludeByOptionContent: FC<ExcludeByOptionContentProps> = ({
    description,
    onSelectCustomExclude,
    popupState,
    setPopupState
}) => {
    const { excludeOption } = popupState ?? {};

    const handleExcludeOptionChange = (option: ExcludeOption) => {
        if (option === ExcludeOption.Custom) {
            onSelectCustomExclude();
        }
        setPopupState({ excludeOption: option });
    };

    return (
        <div className="flex flex-col gap-4 w-full">
            <span className="font-sf">{description}</span>
            <Dropdown
                fullMenuWidth
                variant="trigger"
                className="w-full"
                items={DROPDOWN_ITEMS}
                selectedItems={excludeOption}
                // @ts-ignore - todo: try to remove after - https://silverfort.atlassian.net/browse/AF-35919
                onChange={handleExcludeOptionChange}
            />
        </div>
    );
};

const CustomExcludeContent: FC<CustomExcludeContentProps> = ({
    customExcludeDescription,
    popupState,
    setPopupState
}) => {
    const { customExcludeDateTime } = popupState ?? {};

    const handleChange = (date?: Date) => {
        if (date) {
            setPopupState({ customExcludeDateTime: date });
        }
    };

    return (
        <div className="w-full flex flex-col gap-4">
            <span className="font-sf">{customExcludeDescription}</span>
            <div className="flex gap-2">
                <SingleDatePicker className="!w-[212px]" value={customExcludeDateTime} onChange={handleChange} />
                <TimePicker
                    className="[&.MuiTextField-root]:!w-[212px]"
                    value={customExcludeDateTime}
                    onChange={handleChange}
                    slotProps={{ popper: { className: 'z-[5002] [&>.MuiPickersPopper-paper]:!w-[212px]' } }}
                />
            </div>
        </div>
    );
};

const CustomExcludeFooter: FC<CustomExcludeFooterProps> = ({ popupState, ...props }) => {
    const { customExcludeDateTime } = popupState ?? {};

    const isDoneButtonDisabled = !customExcludeDateTime || new Date(customExcludeDateTime) < new Date();

    return (
        <DefaultFooterComponent
            primaryButtonProps={{
                text: 'Done',
                disabled: isDoneButtonDisabled
            }}
            {...props}
        />
    );
};

const useExcludePopup = ({
    title,
    description,
    customExcludeTitle = 'Custom exclude',
    customExcludeDescription,
    onExclude
}: UseExcludePopupParams): UseExcludePopupReturnType => {
    const { openPopup, updatePopup } = usePopup();

    const handleSaveCustomExclude = useCallback(
        ({ customExcludeDateTime }: CustomExcludePopupState = { customExcludeDateTime: undefined }) => {
            if (customExcludeDateTime) {
                onExclude(customExcludeDateTime);
            }
        },
        [onExclude]
    );

    const handleSelectCustomExclude = useCallback(() => {
        updatePopup({
            title: customExcludeTitle,
            popupClassName: 'w-[480px]',
            ContentComponent: CustomExcludeContent,
            FooterComponent: CustomExcludeFooter,
            onApprove: handleSaveCustomExclude,
            initialState: {
                customExcludeDateTime: undefined
            },
            ContentComponentProps: {
                customExcludeDescription
            }
        });
    }, [customExcludeTitle, customExcludeDescription, handleSaveCustomExclude, updatePopup]);

    const handleSelectExcludeOption = useCallback(
        ({ excludeOption }: ExcludePopupState = { excludeOption: ExcludeOption.OneHour }) => {
            if (excludeOption === ExcludeOption.Custom) {
                handleSelectCustomExclude();
            } else {
                onExclude(getExcludeTillForOption(excludeOption, new Date()));
            }
        },
        [handleSelectCustomExclude, onExclude]
    );

    const openExcludePopup = useCallback(() => {
        openPopup({
            title,
            popupClassName: 'w-[360px]',
            ContentComponent: ExcludeByOptionContent,
            FooterComponent: DefaultFooterComponent,
            initialState: { excludeOption: ExcludeOption.OneHour },
            onApprove: handleSelectExcludeOption,
            ContentComponentProps: {
                description,
                onSelectCustomExclude: handleSelectCustomExclude
            }
        });
    }, [title, description, handleSelectExcludeOption, handleSelectCustomExclude, openPopup]);

    return { openExcludePopup };
};

export default useExcludePopup;
